/**
 * Utility functions for place-related operations
 */

/**
 * Determines if a place is from an API source (not a database place)
 * @param {Object} place - The place object to check
 * @returns {boolean} - True if the place is from an API source
 */
export const isApiPlace = (place) => {
  if (!place) return false;
  
  // Primary indicator: source field from backend
  if (place?.source === 'api') {
    // console.log("[DEBUG] isApiPlace - API place detected by source field");
    return true;
  }
  
  // Secondary indicators for API places
  const hasPlaceUniqueNumber = !!place?.placeUniqueNumber;
  const hasContentId = !!(place?.contentid || place?.contentId);
  const isNotUserRegistered = place?.isUserRegistered === false;
  const hasDbId = !!(place?.id || place?._id);
  
  // console.log("[DEBUG] isApiPlace - place data:", {
  //   source: place?.source,
  //   placeUniqueNumber: place?.placeUniqueNumber,
  //   contentid: place?.contentid,
  //   contentId: place?.contentId,
  //   isUserRegistered: place?.isUserRegistered,
  //   id: place?.id,
  //   _id: place?._id
  // });
  
  // API place if:
  // 1. Has contentId/placeUniqueNumber but no database ID (pure API place), OR
  // 2. Has placeUniqueNumber and is explicitly not user registered
  const result = ((hasPlaceUniqueNumber || hasContentId) && !hasDbId) ||
                 (hasPlaceUniqueNumber && isNotUserRegistered);
  
  // console.log("[DEBUG] isApiPlace - result:", result);
  return result;
};

/**
 * Gets the appropriate identifier for a place (for API or DB places)
 * @param {Object} place - The place object
 * @returns {string|number} - The place identifier
 */
export const getPlaceIdentifier = (place) => {
  if (!place) return null;
  
  // For API places, prefer contentId/placeUniqueNumber
  if (isApiPlace(place)) {
    return place?.placeUniqueNumber || place?.contentid || place?.contentId;
  }
  
  // For DB places, use database ID
  return place?.id || place?._id;
};

/**
 * Compares two place identifiers, handling both API and DB places
 * @param {Object} place1 - First place object
 * @param {Object} place2 - Second place object or item with placeId/placeUniqueNumber
 * @returns {boolean} - True if the places match
 */
export const comparePlaces = (place1, place2) => {
  if (!place1 || !place2) return false;
  
  const place1Id = getPlaceIdentifier(place1);
  
  // If place2 is a list item, it might have placeId or placeUniqueNumber
  const place2Id = place2?.placeUniqueNumber || place2?.placeId || getPlaceIdentifier(place2);
  
  if (!place1Id || !place2Id) return false;
  
  // Convert to strings for comparison to handle type differences
  return String(place1Id) === String(place2Id);
};